import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Search, User } from 'lucide-react';
import { Subscriber } from '../services/subscriberService';

interface SubscriberSelectorProps {
  subscribers: Subscriber[];
  selectedSubscriberId?: number;
  onSelect: (subscriberId: number) => void;
  placeholder?: string;
  isLoading?: boolean;
}

const SubscriberSelector: React.FC<SubscriberSelectorProps> = ({
  subscribers,
  selectedSubscriberId,
  onSelect,
  placeholder = "Selecione um assinante",
  isLoading = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  console.log('SubscriberSelector - subscribers:', subscribers);
  console.log('SubscriberSelector - selectedSubscriberId:', selectedSubscriberId);

  const selectedSubscriber = subscribers.find(subscriber => subscriber.id === selectedSubscriberId);

  const filteredSubscribers = subscribers.filter(subscriber =>
    subscriber.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    subscriber.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSelect = (subscriberId: number) => {
    onSelect(subscriberId);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleToggle = () => {
    if (!isLoading) {
      setIsOpen(!isOpen);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getTypeBadge = (type?: string | null) => {
    if (type === 'business') {
      return (
        <span className="px-1.5 py-0.5 bg-green-100 text-green-800 text-xs rounded">
          PJ
        </span>
      );
    }
    return (
      <span className="px-1.5 py-0.5 bg-blue-100 text-blue-800 text-xs rounded">
        PF
      </span>
    );
  };

  console.log('SubscriberSelector - isOpen:', isOpen);
  console.log('SubscriberSelector - filteredSubscribers:', filteredSubscribers);

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Selected Subscriber Display */}
      <div
        onClick={handleToggle}
        className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gold-500 focus:border-transparent cursor-pointer transition-colors ${
          isLoading ? 'opacity-50 cursor-not-allowed' : 'hover:border-gray-400'
        }`}
      >
        {isLoading ? (
          <div className="flex items-center">
            <div className="text-sm text-gray-500">Carregando assinantes...</div>
          </div>
        ) : selectedSubscriber ? (
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
              <User className="w-4 h-4 text-gray-500" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <span className="text-gray-900">{selectedSubscriber.name}</span>
                {getTypeBadge(selectedSubscriber.type)}
              </div>
              {selectedSubscriber.email && (
                <span className="text-xs text-gray-500">{selectedSubscriber.email}</span>
              )}
            </div>
            <ChevronDown className={`w-5 h-5 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
          </div>
        ) : (
          <div className="flex items-center justify-between">
            <span className="text-gray-500">{placeholder}</span>
            <ChevronDown className={`w-5 h-5 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
          </div>
        )}
      </div>

      {/* Dropdown */}
      {isOpen && !isLoading && (
        <div className="absolute z-[9999] w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-hidden">
          {/* Search Input */}
          <div className="p-2 border-b border-gray-200">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar assinante..."
                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-gold-500 focus:border-transparent"
                autoFocus
              />
            </div>
          </div>

          {/* Subscriber List */}
          <div className="max-h-48 overflow-y-auto">
            {filteredSubscribers.length === 0 ? (
              <div className="p-3 text-sm text-gray-500 text-center">
                {searchTerm ? 'Nenhum assinante encontrado' : 'Nenhum assinante disponível'}
              </div>
            ) : (
              filteredSubscribers.map((subscriber) => (
                <div
                  key={subscriber.id}
                  onClick={() => subscriber.id && handleSelect(subscriber.id)}
                  className={`flex items-center space-x-3 p-3 cursor-pointer transition-colors ${
                    subscriber.id === selectedSubscriberId
                      ? 'bg-gold-50 text-gold-900'
                      : 'hover:bg-gray-50'
                  }`}
                >
                  <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center flex-shrink-0">
                    <User className="w-4 h-4 text-gray-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium truncate">{subscriber.name}</span>
                      {getTypeBadge(subscriber.type)}
                    </div>
                    {subscriber.email && (
                      <div className="text-xs text-gray-500 truncate">{subscriber.email}</div>
                    )}
                  </div>
                  {subscriber.id === selectedSubscriberId && (
                    <div className="w-2 h-2 bg-gold-600 rounded-full flex-shrink-0"></div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriberSelector;
